// Supply Network panel: stateful hook for the "Dealer locations &
// plant assignment" section. Owns:
//   - data (subscriber dealers + dealer locations) loaded via the
//     existing /api/mfg/dealers endpoint.
//   - search + 4 filters (linked org, plant, state, active).
//   - multi-select state.
//   - unified confirm-on-reassign flow for both bulk selection AND
//     per-row "quick reassign" entry points.
//
// Plants are passed in as a prop so the Supply Network panel can share
// the list it already loads for the Plants section (avoids a second
// /api/mfg/plants fetch). Inheritance resolution (direct -> dealer
// org -> org default) lives in dealerLocationPlantResolver.ts.
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  bulkSetManufacturingDealerLocationsPlant,
  listManufacturingDealers,
} from "@/lib/manufacturing/dealersClient";
import type { MfgPlant } from "@/lib/manufacturingClient";
import type {
  MfgDealerAddress,
  MfgDealerLocation,
  MfgSubscriberDealer,
} from "@/lib/manufacturing/dealerTypes";
import {
  buildDealerOrgPlantId,
  buildPlantById,
  defaultPlantOf,
  resolveEffectivePlant,
  type EffectivePlant,
} from "./dealerLocationPlantResolver";

export type DealerLocationPlantFilter = "" | "__unassigned__" | string;
export type DealerLocationLinkedOrgFilter = "" | "__unlinked__" | string;
export type DealerLocationActiveFilter = "active" | "all";

export type PendingReassignment = {
  id: string;
  name: string;
  fromPlantLabel: string;
  toPlantLabel: string;
};

// Internal: a "queued apply" that the confirm modal is gating. Carries
// the IDs + target so the modal's confirm path is independent of
// selection state (per-row reassigns can queue an apply without
// touching selectedIds).
type PendingApply = {
  ids: string[];
  targetPlantId: string | null;
  // True when the apply came from the multi-select bulk bar; on success
  // we then clear `selectedIds` + reset `bulkPlantId`. False for
  // per-row reassigns.
  fromBulk: boolean;
};

export const PLANT_FILTER_UNASSIGNED = "__unassigned__";
export const LINKED_ORG_FILTER_UNLINKED = "__unlinked__";

function addrOf(loc: MfgDealerLocation): MfgDealerAddress {
  return (loc.address as MfgDealerAddress) ?? {};
}
function locationState(loc: MfgDealerLocation): string {
  const v = addrOf(loc).state;
  return typeof v === "string" ? v.trim() : "";
}

export function useManufacturingPlantDealerLocations(
  plants: MfgPlant[],
  refreshSignal: number
) {
  const [subscriberDealers, setSubscriberDealers] = useState<MfgSubscriberDealer[]>([]);
  const [dealerLocations, setDealerLocations] = useState<MfgDealerLocation[]>([]);
  const [listBusy, setListBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [filterPlant, setFilterPlant] = useState<DealerLocationPlantFilter>("");
  const [filterLinkedOrg, setFilterLinkedOrg] =
    useState<DealerLocationLinkedOrgFilter>("");
  const [filterState, setFilterState] = useState("");
  const [filterActive, setFilterActive] = useState<DealerLocationActiveFilter>("active");

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkPlantId, setBulkPlantId] = useState<string>("");
  const [pendingApply, setPendingApply] = useState<PendingApply | null>(null);

  const reload = useCallback(async () => {
    setListBusy(true);
    setLocalError(null);
    const res = await listManufacturingDealers();
    setListBusy(false);
    if (!res.ok) {
      setLocalError(res.error);
      return;
    }
    setSubscriberDealers(res.data.subscriber_dealers);
    setDealerLocations(res.data.dealer_locations);
  }, []);

  useEffect(() => {
    void reload();
  }, [reload, refreshSignal]);

  const plantById = useMemo(() => buildPlantById(plants), [plants]);
  const defaultPlant = useMemo(() => defaultPlantOf(plants), [plants]);
  const dealerOrgPlantId = useMemo(
    () => buildDealerOrgPlantId(subscriberDealers),
    [subscriberDealers]
  );
  const plantNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of plants) map.set(p.id, `${p.plant_code} · ${p.name}`);
    return map;
  }, [plants]);
  const dealerOrgNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of subscriberDealers) map.set(s.dealer_org_id, s.dealer_org_name);
    return map;
  }, [subscriberDealers]);

  // Per-location effective-plant cache; UI reads via effectivePlantFor.
  const effectivePlantByLocationId = useMemo(() => {
    const map = new Map<string, EffectivePlant>();
    for (const loc of dealerLocations)
      map.set(loc.id, resolveEffectivePlant(loc, plantById, dealerOrgPlantId, defaultPlant));
    return map;
  }, [dealerLocations, plantById, dealerOrgPlantId, defaultPlant]);
  const effectivePlantFor = useCallback(
    (locId: string) => effectivePlantByLocationId.get(locId),
    [effectivePlantByLocationId]
  );

  const knownStates = useMemo(() => {
    const set = new Set<string>();
    for (const loc of dealerLocations) {
      const s = locationState(loc);
      if (s) set.add(s.toUpperCase());
    }
    return Array.from(set).sort();
  }, [dealerLocations]);

  const filteredLocations = useMemo(() => {
    const q = search.trim().toLowerCase();
    const wantState = filterState.trim().toUpperCase();
    return dealerLocations.filter((loc) => {
      if (filterActive === "active" && !loc.is_active) return false;

      if (filterPlant === PLANT_FILTER_UNASSIGNED) {
        if (loc.primary_plant_id) return false;
      } else if (filterPlant) {
        if (loc.primary_plant_id !== filterPlant) return false;
      }

      if (filterLinkedOrg === LINKED_ORG_FILTER_UNLINKED) {
        if (loc.linked_dealer_org_id) return false;
      } else if (filterLinkedOrg) {
        if (loc.linked_dealer_org_id !== filterLinkedOrg) return false;
      }

      if (wantState && locationState(loc).toUpperCase() !== wantState) return false;

      if (q) {
        const a = addrOf(loc);
        const orgName = loc.linked_dealer_org_id
          ? (dealerOrgNameById.get(loc.linked_dealer_org_id) ?? "")
          : "";
        const haystack = [
          loc.name,
          loc.contact_name,
          typeof a.city === "string" ? a.city : "",
          typeof a.state === "string" ? a.state : "",
          typeof a.postal_code === "string" ? a.postal_code : "",
          orgName,
        ]
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }

      return true;
    });
  }, [dealerLocations, search, filterPlant, filterLinkedOrg, filterState, filterActive, dealerOrgNameById]);

  const toggleSelected = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const selectAllFiltered = useCallback(() => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const loc of filteredLocations) next.add(loc.id);
      return next;
    });
  }, [filteredLocations]);
  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);
  const allVisibleSelected =
    filteredLocations.length > 0 &&
    filteredLocations.every((loc) => selectedIds.has(loc.id));

  // ----- Reassignment plumbing -----

  const targetPlantId: string | null = bulkPlantId === "" ? null : bulkPlantId;
  const selectedLocations = useMemo(
    () => dealerLocations.filter((loc) => selectedIds.has(loc.id)),
    [dealerLocations, selectedIds]
  );

  // Build a reassignment preview for an arbitrary (ids, targetPlantId)
  // pair. Used by both the bulk-bar's pre-confirm preview AND the
  // confirm modal once an apply is queued.
  const previewReassignments = useCallback(
    (ids: string[], target: string | null): PendingReassignment[] => {
      const label = (pid: string | null) =>
        pid ? (plantNameById.get(pid) ?? pid.slice(0, 8)) : "(no plant)";
      const toLabel = label(target);
      const idSet = new Set(ids);
      return dealerLocations
        .filter((loc) => idSet.has(loc.id))
        .filter((loc) => loc.primary_plant_id && loc.primary_plant_id !== target)
        .map((loc) => ({
          id: loc.id,
          name: loc.name,
          fromPlantLabel: label(loc.primary_plant_id),
          toPlantLabel: toLabel,
        }));
    },
    [dealerLocations, plantNameById]
  );

  // Live preview the user sees in the bulk bar (selection + bulk plant).
  const pendingReassignments = useMemo(
    () => previewReassignments(Array.from(selectedIds), targetPlantId),
    [previewReassignments, selectedIds, targetPlantId]
  );

  const netNewAssignmentCount = selectedLocations.filter(
    (loc) => !loc.primary_plant_id && targetPlantId !== null
  ).length;
  const isNoOp =
    selectedLocations.length === 0 ||
    selectedLocations.every((loc) => (loc.primary_plant_id ?? null) === targetPlantId);

  // Confirm-modal view (driven by the queued apply, not the live
  // selection — so a per-row reassign shows just that one row).
  const confirmOpen = pendingApply !== null;
  const confirmView = useMemo(() => {
    if (!pendingApply)
      return { reassignments: [] as PendingReassignment[], netNewCount: 0, targetLabel: "" };
    const ids = new Set(pendingApply.ids);
    const t = pendingApply.targetPlantId;
    return {
      reassignments: previewReassignments(pendingApply.ids, t),
      netNewCount: dealerLocations.filter(
        (loc) => ids.has(loc.id) && !loc.primary_plant_id && t !== null
      ).length,
      targetLabel: t === null ? "(no plant — clear)" : (plantNameById.get(t) ?? t.slice(0, 8)),
    };
  }, [pendingApply, dealerLocations, plantNameById, previewReassignments]);

  const performApply = useCallback(
    async (apply: PendingApply) => {
      setActionBusy(true);
      setLocalError(null);
      const res = await bulkSetManufacturingDealerLocationsPlant({
        location_ids: apply.ids,
        primary_plant_id: apply.targetPlantId,
      });
      setActionBusy(false);
      if (!res.ok) {
        setLocalError(res.error);
        return;
      }
      if (apply.fromBulk) {
        setSelectedIds(new Set());
        setBulkPlantId("");
      }
      setPendingApply(null);
      await reload();
    },
    [reload]
  );

  const requestBulkApply = useCallback(() => {
    if (selectedLocations.length === 0) return;
    const apply: PendingApply = { ids: Array.from(selectedIds), targetPlantId, fromBulk: true };
    if (pendingReassignments.length > 0) setPendingApply(apply);
    else void performApply(apply);
  }, [selectedLocations.length, selectedIds, targetPlantId, pendingReassignments.length, performApply]);

  // Per-row quick reassign: silent when the row only had an inherited
  // value; gated when the row had a direct primary_plant_id.
  const requestRowReassign = useCallback(
    (loc: MfgDealerLocation, newPlantId: string | null) => {
      const cur = loc.primary_plant_id ?? null;
      if (cur === newPlantId) return;
      const apply: PendingApply = { ids: [loc.id], targetPlantId: newPlantId, fromBulk: false };
      if (cur !== null) setPendingApply(apply);
      else void performApply(apply);
    },
    [performApply]
  );

  const cancelConfirm = useCallback(() => setPendingApply(null), []);
  const submitBulkApply = useCallback(
    async () => { if (pendingApply) await performApply(pendingApply); },
    [pendingApply, performApply]
  );

  return {
    subscriberDealers, dealerLocations, plants, plantNameById, dealerOrgNameById,
    defaultPlant, effectivePlantFor,
    listBusy, actionBusy, localError, reload,
    search, setSearch,
    filterPlant, setFilterPlant,
    filterLinkedOrg, setFilterLinkedOrg,
    filterState, setFilterState,
    filterActive, setFilterActive,
    knownStates, filteredLocations,
    selectedIds, toggleSelected, selectAllFiltered, clearSelection,
    allVisibleSelected, selectedLocations,
    bulkPlantId, setBulkPlantId, targetPlantId,
    pendingReassignments, netNewAssignmentCount, isNoOp,
    requestBulkApply, requestRowReassign,
    confirmOpen,
    confirmReassignments: confirmView.reassignments,
    confirmNetNewCount: confirmView.netNewCount,
    confirmTargetLabel: confirmView.targetLabel,
    cancelConfirm, submitBulkApply,
  };
}

export type UseManufacturingPlantDealerLocations = ReturnType<
  typeof useManufacturingPlantDealerLocations
>;
